// src/services/sentry.ts
import * as Sentry from '@sentry/react';

const JWT_REGEX = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const EMAIL_REGEX = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const CPF_REGEX = /\d{3}\.?\d{3}\.?\d{3}-?\d{2}/g;
const SENSITIVE_PATH_REGEX = /\/(auth|usuario|cliente|login|recuperacao)/i;
const SENSITIVE_KEY_REGEX = /password|senha|token|jwt|cpf|email|authorization/i;

function maskString(value: string): string {
  return value
    .replace(JWT_REGEX, '[JWT]')
    .replace(EMAIL_REGEX, '[EMAIL]')
    .replace(CPF_REGEX, '[CPF]');
}

function maskValue(value: unknown): unknown {
  if (typeof value === 'string') return maskString(value);
  if (Array.isArray(value)) return value.map(maskValue);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SENSITIVE_KEY_REGEX.test(k) ? '[REDACTED]' : maskValue(v);
    }
    return out;
  }
  return value;
}

export function initSentry(): void {
  if (!import.meta.env.PROD) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    sampleRate: 1.0,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request) {
        if (event.request.headers) {
          const headers = event.request.headers as Record<string, string>;
          delete headers.Authorization;
          delete headers.authorization;
        }
        if (event.request.url && SENSITIVE_PATH_REGEX.test(event.request.url) && event.request.data !== undefined) {
          event.request.data = '[REDACTED]';
        } else if (event.request.data !== undefined) {
          event.request.data = maskValue(event.request.data);
        }
      }
      if (event.exception?.values) {
        for (const v of event.exception.values) {
          if (v.value) v.value = maskString(v.value);
        }
      }
      if (typeof event.message === 'string') {
        event.message = maskString(event.message);
      }
      return event;
    },
  });
}
